import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { scoreTone } from "@/lib/constants";
import { History, ChevronRight, Sparkles, Clock, Plus, ArrowLeft } from "lucide-react";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function HistoryModal({ entries, open, onOpenChange }) {
  const [detail, setDetail] = useState(null);

  const close = (o) => {
    if (!o) setDetail(null);
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl font-bold text-slate-900">
            <History className="h-5 w-5 text-indigo-500" />
            {detail ? "Dettaglio ricerca" : "Storico ricerche"}
          </DialogTitle>
          <DialogDescription>
            {detail
              ? `${fmtDate(detail.at)} · ${detail.count} bandi`
              : "Rivedi le ricerche passate e come cambiano i bandi disponibili nel tempo."}
          </DialogDescription>
        </DialogHeader>

        {!detail ? (
          entries.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-slate-500" data-testid="history-empty">
              <Clock className="h-7 w-7 text-slate-300" />
              Nessuna ricerca registrata finora. Avvia una ricerca per iniziare lo storico.
            </div>
          ) : (
            <ul className="space-y-2">
              {entries.map((e) => (
                <li key={e.id}>
                  <button
                    onClick={() => setDetail(e)}
                    data-testid="history-entry"
                    className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left transition-colors hover:border-indigo-300 hover:bg-slate-50"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{fmtDate(e.at)}</span>
                        <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                          e.source === "automatica" ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-500"
                        }`}>
                          {e.source === "automatica" ? "Automatica" : "Manuale"}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500">
                        <span>{e.count} bandi</span>
                        {e.new_count > 0 && (
                          <span className="inline-flex items-center gap-0.5 font-semibold text-emerald-600">
                            <Plus className="h-3 w-3" />{e.new_count} nuovi
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : (
          <div>
            <button onClick={() => setDetail(null)} className="mb-3 flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:underline" data-testid="history-back-button">
              <ArrowLeft className="h-4 w-4" /> Torna allo storico
            </button>
            <ul className="space-y-2">
              {(detail.bandi || []).map((b, i) => {
                const tone = scoreTone(b.compatibilita || 0);
                return (
                  <li key={i} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3" data-testid="history-grant-row">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-800">{b.titolo}</div>
                      <div className="truncate text-xs text-slate-500">{b.ente} · {b.tipologia} · {b.importo_max}</div>
                    </div>
                    <span className={`shrink-0 rounded-md border px-2 py-1 text-xs font-bold ${tone.cls}`}>
                      {b.compatibilita}%
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
